import { getBaseQuery, getBaseUrl, REQUEST_TIMEOUT_MS } from '../api.util.ts'
import { createApi } from '@reduxjs/toolkit/query/react'
import { formatStarScore } from '@/utils/formatters.ts'
import {
  GetBookSnapshotRequest,
  GetBookSnapshotResponse,
  GetBuyingPowerRequest,
  GetBuyingPowerResponse,
  GetUserScoreRequest,
  GetUserScoreResponse,
  GetDailyPnlRequest,
  GetDailyPnlResponse,
  GetDeploymentCurrentRequest,
  GetDeploymentDateRangeRequest,
  GetDeploymentNetExposureRequest,
  GetDeploymentNetExposureResponse,
  GetDeploymentResponse,
  GetFullyPnlRequest,
  GetFullyPnlResponse,
  GetHitRateRequest,
  GetHitRateResponse,
  GetStatisticsRequest,
  GetStatisticsResponse,
  GetTopBottomPerformingIdeasRequest,
  GetTopBottomPerformingIdeasResponse,
  GetTotalNewIdeasRequest,
  GetTotalNewIdeasResponse,
} from './statistics-api.def.ts'

const interfaceName = 'statistics'

export const statisticsApi = createApi({
  reducerPath: 'statisticsApi',
  baseQuery: getBaseQuery(getBaseUrl(interfaceName), REQUEST_TIMEOUT_MS),
  tagTypes: ['statistics', 'current-deployment'],
  endpoints: (build) => ({
    getDailyPnl: build.query<GetDailyPnlResponse, GetDailyPnlRequest>({
      query: (params) => ({
        url: `/${params.bookId}/daily-pnl`,
        method: 'GET',
        params: params,
      }),
    }),
    getTopBottomPerformingIdeas: build.query<
      GetTopBottomPerformingIdeasResponse,
      GetTopBottomPerformingIdeasRequest
    >({
      query: (params) => ({
        url: `/${params.bookId}/top-and-bottom-ideas`,
        method: 'GET',
        params: params,
      }),
    }),
    getHitRate: build.query<GetHitRateResponse, GetHitRateRequest>({
      query: (params) => ({
        url: `/${params.bookId}/hit-rate`,
        method: 'GET',
        params: params,
      }),
    }),
    getDeploymentCurrent: build.query<
      GetDeploymentResponse,
      GetDeploymentCurrentRequest
    >({
      query: (params) => ({
        url: `/deployment/${params.bookId}/current`,
        method: 'GET',
        params: params,
      }),
      providesTags: ['current-deployment'],
    }),
    getDeploymentDateRange: build.query<
      GetDeploymentResponse,
      GetDeploymentDateRangeRequest
    >({
      query: (params) => ({
        url: `/deployment/${params.bookId}/average`,
        method: 'GET',
        params: params,
      }),
      providesTags: ['statistics'],
    }),
    getTotalNewIdeasDateRange: build.query<
      GetTotalNewIdeasResponse,
      GetTotalNewIdeasRequest
    >({
      query: (params) => ({
        url: `/${params.bookId}/total-new-ideas`,
        method: 'GET',
        params: params,
      }),
    }),
    getStatistics: build.query<GetStatisticsResponse, GetStatisticsRequest>({
      query: (params) => ({
        url: '/',
        method: 'GET',
        params: params,
        // Opts out of the shared 30s cap: this report is allowed to run long.
        // Must be 0, not undefined — undefined falls back to the default.
        timeout: 0,
      }),
    }),
    getBuyingPower: build.query<GetBuyingPowerResponse, GetBuyingPowerRequest>({
      query: (params) => ({
        url: `/${params.bookId}/buying-power`,
        method: 'GET',
        params: params,
      }),
      providesTags: ['statistics'],
    }),
    getNetExposure: build.query<
      GetDeploymentNetExposureResponse,
      GetDeploymentNetExposureRequest
    >({
      query: (params) => ({
        url: `/${params.bookId}/net-exposure`,
        method: 'GET',
        params: params,
      }),
      providesTags: ['statistics'],
    }),
    getFullyPnl: build.query<GetFullyPnlResponse, GetFullyPnlRequest>({
      query: (params) => ({
        url: `/${params.bookId}/fully-pnl`,
        method: 'GET',
        params: params,
      }),
      providesTags: ['statistics'],
    }),
    getBookSnapshot: build.query<
      GetBookSnapshotResponse,
      GetBookSnapshotRequest
    >({
      query: (params) => ({
        url: `/${params.bookId}/snapshot/${params.date}`,
        method: 'GET',
        params: params,
      }),
      transformResponse: (
        response: GetBookSnapshotResponse
      ): GetBookSnapshotResponse => ({
        data: {
          ...response.data,
          latestScore: formatStarScore(response.data.latestScore),
          previousScore: formatStarScore(response.data.previousScore),
        },
      }),
    }),
    getUserScore: build.query<GetUserScoreResponse, GetUserScoreRequest>({
      query: (params) => ({
        url: `/${params.bookId}/user-score`,
        method: 'GET',
        params: params,
      }),
      transformResponse: (
        response: GetUserScoreResponse
      ): GetUserScoreResponse => ({
        data: {
          ...response.data,
          latestScore: formatStarScore(response.data.latestScore),
          previousScore: formatStarScore(response.data.previousScore),
        },
      }),
      providesTags: ['statistics'],
    }),
  }),
})

export const {
  useGetDailyPnlQuery,
  useGetTopBottomPerformingIdeasQuery,
  useGetHitRateQuery,
  useGetDeploymentCurrentQuery,
  useGetDeploymentDateRangeQuery,
  useGetTotalNewIdeasDateRangeQuery,
  useGetStatisticsQuery,
  useGetBuyingPowerQuery,
  useGetNetExposureQuery,
  useGetFullyPnlQuery,
  useLazyGetBookSnapshotQuery,
  useGetUserScoreQuery,
} = statisticsApi
